import { useEffect, useRef, useState } from 'react';

const MAX_ZOOM = 4;
const TAP_ZOOM = 2.5;
const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
const mid = (t) => ({ x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 });

// Gesti della galleria: scorrere fra le foto, pinch/doppio tocco per lo zoom,
// trascinare in giù per chiudere. `view` = zoom della foto corrente.
export function useGalleryGestures({ count, idx, setIdx, onClose, stageRef }) {
  const [view, setView] = useState({ s: 1, x: 0, y: 0 });
  const [drag, setDrag] = useState({ x: 0, y: 0 });
  const [live, setLive] = useState(false);
  const g = useRef({});
  const viewRef = useRef(view);
  viewRef.current = view;
  const lastTap = useRef(0);
  const lastTouch = useRef(0);

  useEffect(() => { setView({ s: 1, x: 0, y: 0 }); }, [idx]);

  const box = () => {
    const r = stageRef.current.getBoundingClientRect();
    return { w: r.width, h: r.height, cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
  };
  const clamp = (v) => {
    const { w, h } = box();
    const mx = (w * (v.s - 1)) / 2;
    const my = (h * (v.s - 1)) / 2;
    return { s: v.s, x: Math.max(-mx, Math.min(mx, v.x)), y: Math.max(-my, Math.min(my, v.y)) };
  };
  const zoomAt = (x, y) => {
    if (viewRef.current.s > 1) { setView({ s: 1, x: 0, y: 0 }); return; }
    const { cx, cy } = box();
    setView(clamp({ s: TAP_ZOOM, x: (cx - x) * (TAP_ZOOM - 1), y: (cy - y) * (TAP_ZOOM - 1) }));
  };

  const onTouchStart = (e) => {
    const t = e.touches;
    lastTouch.current = Date.now();
    setLive(true);
    if (t.length === 2) {
      g.current = { mode: 'pinch', d0: dist(t), m0: mid(t), v0: viewRef.current };
      setDrag({ x: 0, y: 0 });
    } else if (t.length === 1) {
      g.current = { mode: null, x0: t[0].clientX, y0: t[0].clientY, t0: Date.now(), v0: viewRef.current };
    }
  };

  const onTouchMove = (e) => {
    const t = e.touches;
    const st = g.current;
    if (st.mode === 'pinch' && t.length === 2) {
      const { cx, cy } = box();
      const m = mid(t);
      const s = Math.min(MAX_ZOOM, Math.max(1, (st.v0.s * dist(t)) / st.d0));
      const k = s / st.v0.s;
      setView(clamp({ s, x: m.x - cx - (st.m0.x - cx - st.v0.x) * k, y: m.y - cy - (st.m0.y - cy - st.v0.y) * k }));
      return;
    }
    if (t.length !== 1 || st.mode === 'pinch' || st.x0 == null) return;
    const dx = t[0].clientX - st.x0;
    const dy = t[0].clientY - st.y0;
    if (!st.mode) {
      if (Math.hypot(dx, dy) < 8) return;
      if (st.v0.s > 1) st.mode = 'pan';
      else if (Math.abs(dx) > Math.abs(dy)) st.mode = 'swipe';
      else st.mode = dy > 0 ? 'dismiss' : 'none';
    }
    if (st.mode === 'pan') setView(clamp({ s: st.v0.s, x: st.v0.x + dx, y: st.v0.y + dy }));
    else if (st.mode === 'swipe') {
      const edge = (idx === 0 && dx > 0) || (idx === count - 1 && dx < 0);
      setDrag({ x: edge ? dx / 3 : dx, y: 0 });
    } else if (st.mode === 'dismiss') setDrag({ x: 0, y: Math.max(0, dy) });
  };

  const onTouchEnd = (e) => {
    const st = g.current;
    if (e.touches.length > 0) {
      // Dal pinch resta un dito: si continua a spostare la foto ingrandita
      if (st.mode === 'pinch' && e.touches.length === 1) {
        const t = e.touches[0];
        g.current = { mode: 'pan', x0: t.clientX, y0: t.clientY, v0: viewRef.current };
      }
      return;
    }
    setLive(false);
    setDrag({ x: 0, y: 0 });
    g.current = {};
    if (st.mode === 'pinch') {
      if (viewRef.current.s < 1.05) setView({ s: 1, x: 0, y: 0 });
      return;
    }
    if (st.x0 == null) return;
    const ct = e.changedTouches[0];
    const dx = ct.clientX - st.x0;
    const dy = ct.clientY - st.y0;
    const dt = Math.max(1, Date.now() - (st.t0 || 0));
    if (st.mode === 'swipe') {
      if (Math.abs(dx) > box().w * 0.2 || Math.abs(dx) / dt > 0.5) {
        if (dx < 0 && idx < count - 1) setIdx(idx + 1);
        else if (dx > 0 && idx > 0) setIdx(idx - 1);
      }
    } else if (st.mode === 'dismiss') {
      if (dy > 120 || (dy > 50 && dy / dt > 0.6)) onClose();
    } else if (!st.mode && dt < 300) {
      const now = Date.now();
      if (now - lastTap.current < 300) { zoomAt(ct.clientX, ct.clientY); lastTap.current = 0; }
      else lastTap.current = now;
    }
  };

  // Desktop: doppio clic per lo zoom (ignorato subito dopo un tocco)
  const onDoubleClick = (e) => {
    if (Date.now() - lastTouch.current < 800) return;
    zoomAt(e.clientX, e.clientY);
  };

  return { view, drag, live, handlers: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel: onTouchEnd, onDoubleClick } };
}
